import React from 'react';
import { Controller, useFieldArray } from 'react-hook-form';

import FormSectionCard from '~/componentes/FormSectionCard';
import InputText from '~/componentes/Inputs/InputText';
import InputTextarea from '~/componentes/Inputs/InputTextarea';
import InputSelect from '~/componentes/Inputs/InputSelect';
import InputDate from '~/componentes/Inputs/InputDate';
import InputRadio from '~/componentes/Inputs/InputRadio';
import IconLixeira from '~/assets/icones/IconLixeira.svg';

import setRegex from '~/utils/setRegex';
import { Colors } from '~/config/Colors';
import { tipoContaOptions } from '../formOptions';
import usePerfilAutoFill from '../usePerfilAutoFill';
import useDominioOptions from '../useDominioOptions';
import * as S from './styles';

function formatDataNascimento(value) {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date)) return '';
  return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

function FuncionarioFormFields({ register, control, errors, watch, isEditing = false }) {
  const idPerfil = watch('idPerfil');
  const { perfilOptions, perfilData, loadingPerfil } = usePerfilAutoFill({ idPerfil });
  const { generoOptions, racaCorOptions, nacionalidadeOptions, estadoOptions, loadingDominios } =
    useDominioOptions();

  const usuario = perfilData?.usuario;

  const {
    fields: fieldsDocumentos,
    append: appendDocumento,
    remove: removeDocumento,
  } = useFieldArray({ control, name: 'documentos' });

  return (
    <S.BodyPageContainer>
      <FormSectionCard
        title="1. Dados Pessoais"
        subTitle="Vínculo com o Perfil e dados pessoais"
        isLine
      >
        <S.CardBodyContainer>
          <S.FullWidthField>
            <Controller
              control={control}
              name="idPerfil"
              render={({ field }) => (
                <InputSelect
                  label="Perfil do Usuário *"
                  placeholder={
                    loadingPerfil ? 'Carregando...' : 'Selecione um perfil já cadastrado'
                  }
                  options={perfilOptions}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.idPerfil?.message}
                  isDisabled={loadingPerfil || isEditing}
                  required
                />
              )}
            />
            {/* <S.HelperText>
              Nome, CPF, data de nascimento, e-mail e telefone vêm do Perfil selecionado e são
              somente leitura aqui.
            </S.HelperText> */}
          </S.FullWidthField>

          <InputText label="Nome Completo" value={usuario?.nome || ''} disabled readOnly />
          <InputText
            label="CPF"
            value={usuario?.cpf ? setRegex.setCpf(usuario.cpf) : ''}
            disabled
            readOnly
          />
          <InputText
            label="Data de Nascimento"
            value={formatDataNascimento(usuario?.dataNascimento)}
            disabled
            readOnly
          />
          <InputText label="E-mail" value={usuario?.email || ''} disabled readOnly />
          <InputText
            label="Telefone"
            value={usuario?.telefone1 ? setRegex.setPhone(usuario.telefone1) : ''}
            disabled
            readOnly
          />

          <Controller
            control={control}
            name="idGenero"
            render={({ field }) => (
              <InputSelect
                label="Gênero"
                placeholder={loadingDominios ? 'Carregando...' : 'Selecione'}
                options={generoOptions}
                value={field.value}
                onChange={field.onChange}
                error={errors.idGenero?.message}
                isDisabled={loadingDominios}
              />
            )}
          />
          <Controller
            control={control}
            name="idNacionalidade"
            render={({ field }) => (
              <InputSelect
                label="Nacionalidade"
                placeholder={loadingDominios ? 'Carregando...' : 'Selecione'}
                options={nacionalidadeOptions}
                value={field.value}
                onChange={field.onChange}
                error={errors.idNacionalidade?.message}
                isDisabled={loadingDominios}
              />
            )}
          />
          <InputText
            label="Naturalidade (Cidade)"
            placeholder="Ex: São Paulo"
            {...register('cidadeNatal')}
            error={errors.cidadeNatal?.message}
          />
          <Controller
            control={control}
            name="idEstadoNatal"
            render={({ field }) => (
              <InputSelect
                label="Naturalidade (Estado)"
                placeholder={loadingDominios ? 'Carregando...' : 'Selecione'}
                options={estadoOptions}
                value={field.value}
                onChange={field.onChange}
                error={errors.idEstadoNatal?.message}
                isDisabled={loadingDominios}
              />
            )}
          />
        </S.CardBodyContainer>

        <S.RadiosContainer>
          <S.RadioLabel>Raça/Cor (Autodeclaração)</S.RadioLabel>
          <S.RadioContent>
            {loadingDominios && racaCorOptions.length === 0 && (
              <S.HelperText>Carregando...</S.HelperText>
            )}
            {racaCorOptions.map(option => (
              <InputRadio
                key={option.value}
                label={option.label}
                value={option.value}
                {...register('idRacaCor')}
              />
            ))}
          </S.RadioContent>
          {errors.idRacaCor && (
            <span style={{ color: Colors.ftColor6, fontSize: 12 }}>{errors.idRacaCor.message}</span>
          )}
        </S.RadiosContainer>
      </FormSectionCard>

      <FormSectionCard
        title="2. Documentação"
        subTitle="Documentos pessoais e profissionais"
        isLine
      >
        <S.CardBodyContainer>
          <InputText
            label="RG"
            placeholder="Número do RG"
            {...register('rg')}
            error={errors.rg?.message}
          />
          <InputText
            label="Órgão Emissor"
            placeholder="Ex: SSP"
            {...register('orgaoEmissor')}
            error={errors.orgaoEmissor?.message}
          />
          <InputText
            label="UF (RG)"
            placeholder="UF"
            maxLength={2}
            {...register('ufRg')}
            error={errors.ufRg?.message}
          />
          <InputText
            label="PIS/PASEP"
            placeholder="PIS/PASEP"
            {...register('pisPasep')}
            error={errors.pisPasep?.message}
          />
          <InputText
            label="CTPS"
            placeholder="Número da CTPS"
            {...register('ctps')}
            error={errors.ctps?.message}
          />
          <InputText
            label="Série (CTPS)"
            placeholder="Série"
            {...register('serieCtps')}
            error={errors.serieCtps?.message}
          />
          <InputText
            label="UF (CTPS)"
            placeholder="UF"
            maxLength={2}
            {...register('ufCtps')}
            error={errors.ufCtps?.message}
          />
          <InputText
            label="Título de Eleitor"
            placeholder="Número do título"
            {...register('tituloEleitor')}
            error={errors.tituloEleitor?.message}
          />
          <InputText
            label="Zona Eleitoral"
            placeholder="Zona"
            {...register('zonaEleitoral')}
            error={errors.zonaEleitoral?.message}
          />
          <InputText
            label="Seção Eleitoral"
            placeholder="Seção"
            {...register('secaoEleitoral')}
            error={errors.secaoEleitoral?.message}
          />
          <InputText
            label="Certificado de Reservista"
            placeholder="Se aplicável"
            {...register('certificadoReservista')}
            error={errors.certificadoReservista?.message}
          />
          <InputText
            label="CNH (Número)"
            placeholder="Número da CNH"
            {...register('cnh')}
            error={errors.cnh?.message}
          />
          <InputText
            label="CNH (Categoria)"
            placeholder="Ex: AB"
            {...register('categoriaCnh')}
            error={errors.categoriaCnh?.message}
          />
          <Controller
            control={control}
            name="validadeCnh"
            render={({ field }) => (
              <InputDate
                label="CNH (Validade)"
                placeholder="Selecione a data"
                error={errors.validadeCnh?.message}
                required={false}
                {...field}
              />
            )}
          />
        </S.CardBodyContainer>
      </FormSectionCard>

      <FormSectionCard
        title="3. Endereço e Filiação"
        subTitle="Endereço residencial e filiação"
        isLine
      >
        <S.CardBodyContainer>
          <S.FullWidthField>
            <InputText
              label="Endereço"
              placeholder="Rua, Av, Nº, Apto"
              {...register('endereco')}
              error={errors.endereco?.message}
            />
          </S.FullWidthField>
          <InputText
            label="Bairro"
            placeholder="Bairro"
            {...register('bairro')}
            error={errors.bairro?.message}
          />
          <Controller
            control={control}
            name="cep"
            render={({ field }) => (
              <InputText
                label="CEP"
                placeholder="00000-000"
                value={field.value}
                onChange={e => field.onChange(setRegex.setCep(e.target.value))}
                error={errors.cep?.message}
              />
            )}
          />
          <InputText
            label="Cidade"
            placeholder="Cidade"
            {...register('cidade')}
            error={errors.cidade?.message}
          />
          <Controller
            control={control}
            name="idEstado"
            render={({ field }) => (
              <InputSelect
                label="Estado"
                placeholder={loadingDominios ? 'Carregando...' : 'Selecione'}
                options={estadoOptions}
                value={field.value}
                onChange={field.onChange}
                error={errors.idEstado?.message}
                isDisabled={loadingDominios}
              />
            )}
          />
          <InputText
            label="Nome da Mãe (Filiação 1)"
            placeholder="Nome completo da mãe"
            {...register('nomeMae')}
            error={errors.nomeMae?.message}
          />
          <InputText
            label="Nome do Pai (Filiação 2)"
            placeholder="Nome completo do pai"
            {...register('nomePai')}
            error={errors.nomePai?.message}
          />
          <InputText
            label="Contato de Emergência (Nome)"
            placeholder="Nome do contato"
            {...register('contatoEmergenciaNome')}
            error={errors.contatoEmergenciaNome?.message}
          />
          <Controller
            control={control}
            name="contatoEmergenciaTelefone"
            render={({ field }) => (
              <InputText
                label="Contato de Emergência (Telefone)"
                placeholder="(00) 00000-0000"
                value={field.value}
                onChange={e => field.onChange(setRegex.setPhone(e.target.value))}
                error={errors.contatoEmergenciaTelefone?.message}
              />
            )}
          />
        </S.CardBodyContainer>
      </FormSectionCard>

      <FormSectionCard title="4. Dados Bancários" subTitle="Informações para pagamento" isLine>
        <S.CardBodyContainer>
          <InputText
            label="Banco"
            placeholder="Nome do banco"
            {...register('banco')}
            error={errors.banco?.message}
          />
          <InputText
            label="Agência"
            placeholder="Agência"
            {...register('agencia')}
            error={errors.agencia?.message}
          />
          <InputText
            label="Conta"
            placeholder="Número da conta"
            {...register('conta')}
            error={errors.conta?.message}
          />
          <InputText
            label="Chave PIX"
            placeholder="Chave PIX (se houver)"
            {...register('chavePix')}
            error={errors.chavePix?.message}
          />
        </S.CardBodyContainer>

        <S.RadiosContainer>
          <S.RadioLabel>Tipo de Conta</S.RadioLabel>
          <S.RadioContent>
            {tipoContaOptions.map(option => (
              <InputRadio
                key={option.value}
                label={option.label}
                value={option.value}
                {...register('tipoConta')}
              />
            ))}
          </S.RadioContent>
        </S.RadiosContainer>
      </FormSectionCard>

      <FormSectionCard
        title="5. Histórico Profissional"
        subTitle="Cargo atual e desenvolvimento"
        isLine
      >
        <S.CardBodyContainer>
          <InputText
            label="Última Empresa"
            placeholder="Empresa anterior"
            {...register('ultimaEmpresa')}
            error={errors.ultimaEmpresa?.message}
          />
          <InputText
            label="Cargo Anterior"
            placeholder="Cargo na empresa anterior"
            {...register('cargoAnterior')}
            error={errors.cargoAnterior?.message}
          />
          <Controller
            control={control}
            name="periodoPermanenciaInicio"
            render={({ field }) => (
              <InputDate
                label="Período de Permanência - Início (aproximado)"
                placeholder="Selecione a data"
                error={errors.periodoPermanenciaInicio?.message}
                required={false}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="periodoPermanenciaFim"
            render={({ field }) => (
              <InputDate
                label="Período de Permanência - Fim (aproximado)"
                placeholder="Selecione a data"
                error={errors.periodoPermanenciaFim?.message}
                required={false}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="dataAdmissao"
            render={({ field }) => (
              <InputDate
                label="Data de Admissão *"
                placeholder="Selecione a data"
                error={errors.dataAdmissao?.message}
                required
                {...field}
              />
            )}
          />
          <InputText
            label="Cargo Atual *"
            placeholder="Cargo atual"
            {...register('cargoAtual')}
            error={errors.cargoAtual?.message}
            required
          />
          <InputText
            label="Departamento *"
            placeholder="Departamento"
            {...register('departamento')}
            error={errors.departamento?.message}
            required
          />
          <S.FullWidthField>
            <InputTextarea
              label="Cursos Realizados e Especializações"
              placeholder="Cursos e especializações"
              rows={3}
              {...register('cursosEspecializacoes')}
              error={errors.cursosEspecializacoes?.message}
            />
          </S.FullWidthField>
        </S.CardBodyContainer>
      </FormSectionCard>

      <FormSectionCard
        title="6. Atividades"
        subTitle="Principais atividades, atribuições e responsabilidades"
        isLine
      >
        <InputTextarea
          label="Descrição detalhada das funções desempenhadas"
          placeholder="Descreva as atividades do colaborador"
          rows={5}
          {...register('descricaoAtividades')}
          error={errors.descricaoAtividades?.message}
        />
      </FormSectionCard>

      <FormSectionCard
        title="7. Documentos"
        subTitle="Links de documentos em PDF (contrato, RG, comprovantes, etc.)"
        isLine
        onClickButton={() => appendDocumento({ url: '' })}
      >
        <S.DocumentosContainer>
          {fieldsDocumentos.length === 0 && (
            <S.HelperText>Nenhum documento adicionado.</S.HelperText>
          )}
          {fieldsDocumentos.map((field, index) => {
            const url = watch(`documentos.${index}.url`);

            return (
              <S.DocumentoRow key={field.id}>
                <div style={{ flex: 1 }}>
                  <InputText
                    placeholder="https://.../documento.pdf"
                    {...register(`documentos.${index}.url`)}
                    error={errors.documentos?.[index]?.url?.message}
                  />
                </div>
                <S.LinkButton
                  href={url || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Abrir documento"
                  onClick={e => {
                    if (!url) e.preventDefault();
                  }}
                  style={{ opacity: url ? 1 : 0.4, pointerEvents: url ? 'auto' : 'none' }}
                >
                  <i className="material-icons" style={{ fontSize: '20px' }}>
                    open_in_new
                  </i>
                </S.LinkButton>
                <S.DeleteButton
                  title="Remover"
                  type="button"
                  onClick={() => removeDocumento(index)}
                >
                  <img src={IconLixeira} alt="lixeira" />
                </S.DeleteButton>
              </S.DocumentoRow>
            );
          })}
        </S.DocumentosContainer>
      </FormSectionCard>
    </S.BodyPageContainer>
  );
}

export default FuncionarioFormFields;
